import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, PawPrint, MapPin, Phone, Clock } from "lucide-react";
import { CLINICS, SOCIAIS } from "../../data/clinics";
import WhatsAppFloat from "../WhatsAppFloat";

const QUICK_LINKS = [
  { label: "Sobre", to: "/sobre" },
  { label: "Serviços", to: "/servicos" },
  { label: "Marcar Consulta", to: "/marcar-consulta" },
  { label: "Contactos", to: "/contactos" },
];

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0F3A44] text-[#FAF7F2]"
    >
      <div className="container-app py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Logo + tagline */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-flex items-center gap-2">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#E8A87C] text-[#0F3A44]">
                <PawPrint className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <span className="font-display text-2xl font-extrabold tracking-tight">
                Vet Clinic
              </span>
            </Link>
            <p className="mt-5 text-sm text-white/70 max-w-xs leading-relaxed">
              Cuidados veterinários próximos, profissionais e cheios de
              carinho. Na Ribeira Brava e na Calheta.
            </p>

            <div className="mt-7 flex items-center gap-3">
              <a
                href={SOCIAIS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="footer-instagram"
                className="grid h-10 w-10 place-items-center rounded-full bg-white/8 border border-white/10 hover:bg-[#E8A87C] hover:text-[#0F3A44] hover:border-[#E8A87C] transition-colors"
              >
                <Instagram className="h-4.5 w-4.5" />
              </a>
              <a
                href={SOCIAIS.facebookRibeira}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook Ribeira Brava"
                data-testid="footer-facebook-ribeira"
                className="grid h-10 w-10 place-items-center rounded-full bg-white/8 border border-white/10 hover:bg-[#E8A87C] hover:text-[#0F3A44] hover:border-[#E8A87C] transition-colors"
              >
                <Facebook className="h-4.5 w-4.5" />
              </a>
              <a
                href={SOCIAIS.facebookCalheta}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook Calheta"
                data-testid="footer-facebook-calheta"
                className="grid h-10 w-10 place-items-center rounded-full bg-white/8 border border-white/10 hover:bg-[#E8A87C] hover:text-[#0F3A44] hover:border-[#E8A87C] transition-colors"
              >
                <Facebook className="h-4.5 w-4.5" />
              </a>
              <a
                href={`mailto:${SOCIAIS.email}`}
                aria-label="Email"
                data-testid="footer-email"
                className="grid h-10 w-10 place-items-center rounded-full bg-white/8 border border-white/10 hover:bg-[#E8A87C] hover:text-[#0F3A44] hover:border-[#E8A87C] transition-colors"
              >
                <Mail className="h-4.5 w-4.5" />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div className="lg:col-span-2">
            <h4 className="font-display text-sm font-bold uppercase tracking-widest text-white/60 mb-5">
              Navegar
            </h4>
            <ul className="space-y-3">
              {QUICK_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-[15px] text-white/85 hover:text-[#E8A87C] transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Clinics */}
          <div className="lg:col-span-4">
            <h4 className="font-display text-sm font-bold uppercase tracking-widest text-white/60 mb-5">
              Clínicas
            </h4>
            <div className="space-y-6">
              {CLINICS.map((c) => (
                <div key={c.slug} className="text-[14px]">
                  <div className="font-bold text-white">{c.nome}</div>
                  <div className="mt-2 space-y-1.5 text-white/70">
                    <div className="flex gap-2">
                      <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#88A096]" />
                      <span>
                        {c.morada}, {c.codigoPostal} {c.cidade}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Phone className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#88A096]" />
                      <a
                        href={`tel:${c.telefoneE164}`}
                        className="hover:text-[#E8A87C] transition-colors"
                      >
                        {c.telefone}
                      </a>
                    </div>
                    <div className="flex gap-2">
                      <Clock className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#88A096]" />
                      <span>{c.horarioResumo}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact direct */}
          <div className="lg:col-span-2">
            <h4 className="font-display text-sm font-bold uppercase tracking-widest text-white/60 mb-5">
              Contacto
            </h4>
            <div className="space-y-3 text-[14px]">
              <a
                href={`mailto:${SOCIAIS.email}`}
                className="block text-white/85 hover:text-[#E8A87C] transition-colors break-all"
              >
                {SOCIAIS.email}
              </a>
              <Link
                to="/marcar-consulta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#E8A87C] text-[#1A1A1A] px-5 py-2.5 text-sm font-bold hover:bg-[#D99A6E] transition-colors mt-2"
              >
                Marcar consulta
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/8">
        <div className="container-app py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/55">
          <div>
            © 2026 Vet Clinic. Todos os direitos reservados.
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link
              to="/politica-de-privacidade"
              className="hover:text-[#E8A87C] transition-colors"
            >
              Política de Privacidade
            </Link>
            <Link
              to="/termos-e-condicoes"
              className="hover:text-[#E8A87C] transition-colors"
            >
              Termos e Condições
            </Link>
          </div>
          <div className="text-white/45">
            Site desenvolvido com cuidado para os animais da Madeira
          </div>
        </div>
      </div>
      <WhatsAppFloat />
    </footer>
  );
};

export default Footer;
